import { z } from "zod";
import { Site } from "@/bryntum-lib/models/Site";
import { mergeSitePublishFromApi } from "@/react/components/entityViews/marketingPages/utils/sitePublishState";

const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Site form schema — domain, canonical URL, and theme are server-managed (not edited in UI). */
export const siteSchema = z.object({
  slug: z
    .string()
    .min(1, "Slug is required")
    .regex(slugPattern, "Use lowercase letters, numbers, and hyphens only"),
  metaTitle: z.string(),
  metaDescription: z.string(),
  robotsIndex: z.boolean(),
  robotsFollow: z.boolean(),
  ogTitle: z.string(),
  ogDescription: z.string(),
  ogImageUrl: z.string(),
  twitterCard: z.string().min(1, "Twitter card type is required"),
});

export type SiteFormData = z.infer<typeof siteSchema>;

export function siteToFormData(site: Site): SiteFormData {
  return {
    slug: site.slug ?? "",
    metaTitle: site.metaTitle ?? "",
    metaDescription: site.metaDescription ?? "",
    robotsIndex: site.robotsIndex !== false,
    robotsFollow: site.robotsFollow !== false,
    ogTitle: site.ogTitle ?? "",
    ogDescription: site.ogDescription ?? "",
    ogImageUrl: site.ogImageUrl ?? "",
    twitterCard: site.twitterCard ?? "summary_large_image",
  };
}

export function applySiteFormData(site: Site, data: SiteFormData): void {
  site.set({
    slug: data.slug,
    metaTitle: data.metaTitle,
    metaDescription: data.metaDescription,
    robotsIndex: data.robotsIndex,
    robotsFollow: data.robotsFollow,
    ogTitle: data.ogTitle,
    ogDescription: data.ogDescription,
    twitterCard: data.twitterCard,
  });
}

/** Apply OG image fields from upload/delete API response (read-only in Bryntum sync). */
export function mergeOgImageApiIntoSite(
  site: Site,
  apiSite: Partial<Record<string, unknown>>,
): void {
  site.set({
    ogImageUrl: apiSite.og_image_url != null ? String(apiSite.og_image_url) : "",
    ogImageThumbnailUrl:
      apiSite.og_image_thumbnail_url != null ? String(apiSite.og_image_thumbnail_url) : "",
  });
  mergeSitePublishFromApi(site, apiSite);
}

/** Apply favicon fields from upload/delete API response (read-only in Bryntum sync). */
export function mergeFaviconApiIntoSite(
  site: Site,
  apiSite: Partial<Record<string, unknown>>,
): void {
  site.set({
    faviconUrl: apiSite.favicon_url != null ? String(apiSite.favicon_url) : "",
    faviconThumbnailUrl:
      apiSite.favicon_thumbnail_url != null ? String(apiSite.favicon_thumbnail_url) : "",
  });
  mergeSitePublishFromApi(site, apiSite);
}

export function mergeSiteApiIntoFormData(
  current: SiteFormData,
  apiSite: Partial<Record<string, unknown>>,
): SiteFormData {
  const str = (key: keyof SiteFormData, apiKey: string): string => {
    const val = apiSite[apiKey];
    if (val == null || val === "") return current[key] as string;
    return String(val);
  };

  return {
    slug: str("slug", "slug"),
    metaTitle: str("metaTitle", "meta_title"),
    metaDescription: str("metaDescription", "meta_description"),
    robotsIndex: apiSite.robots_index != null ? Boolean(apiSite.robots_index) : current.robotsIndex,
    robotsFollow: apiSite.robots_follow != null ? Boolean(apiSite.robots_follow) : current.robotsFollow,
    ogTitle: str("ogTitle", "og_title"),
    ogDescription: str("ogDescription", "og_description"),
    ogImageUrl: str("ogImageUrl", "og_image_url"),
    twitterCard: str("twitterCard", "twitter_card"),
  };
}
